import { AcrCategory, AcrProbability, AcrSeverity, AcrStatus, type StudyAcrEntrySelectDto } from '@@types/api.types';
import { type BadgeProps } from '@components/ui/badge';

const CATEGORY_LETTERS: Readonly<Record<AcrCategory, string>> = {
  [AcrCategory.ASSUMPTION]: 'A',
  [AcrCategory.CONSTRAINT]: 'C',
  [AcrCategory.RISK]: 'R',
};

const CATEGORY_BADGE_VARIANT: Readonly<Record<AcrCategory, BadgeProps['variant']>> = {
  [AcrCategory.ASSUMPTION]: 'intelligence',
  [AcrCategory.CONSTRAINT]: 'warning',
  [AcrCategory.RISK]: 'destructive',
};

const CATEGORY_TOOLTIPS: Readonly<Record<AcrCategory, string>> = {
  [AcrCategory.ASSUMPTION]: 'Assumption — a condition taken as true for the analysis to hold.',
  [AcrCategory.CONSTRAINT]: 'Constraint — a limit or condition imposed on the study.',
  [AcrCategory.RISK]: 'Risk — an uncertain condition that could degrade mission success.',
};

const STATUS_LABELS: Readonly<Record<AcrStatus, string>> = {
  [AcrStatus.ACCEPTED]: 'Accepted',
  [AcrStatus.CLOSED]: 'Closed',
  [AcrStatus.MITIGATED]: 'Mitigated',
  [AcrStatus.OPEN]: 'Open',
};

const STATUS_BADGE_VARIANT: Readonly<Record<AcrStatus, BadgeProps['variant']>> = {
  [AcrStatus.ACCEPTED]: 'pending',
  [AcrStatus.CLOSED]: 'pending',
  [AcrStatus.MITIGATED]: 'success',
  [AcrStatus.OPEN]: 'warning',
};

const PROBABILITY_LABELS: Readonly<Record<AcrProbability, string>> = {
  [AcrProbability.HIGH]: 'High',
  [AcrProbability.LOW]: 'Low',
  [AcrProbability.MEDIUM]: 'Medium',
  [AcrProbability.VERY_HIGH]: 'Very High',
  [AcrProbability.VERY_LOW]: 'Very Low',
};

const SEVERITY_LABELS: Readonly<Record<AcrSeverity, string>> = {
  [AcrSeverity.CRITICAL]: 'Critical',
  [AcrSeverity.HIGH]: 'High',
  [AcrSeverity.LOW]: 'Low',
  [AcrSeverity.MEDIUM]: 'Medium',
};

const SEVERITY_BADGE_VARIANT: Readonly<Record<AcrSeverity, BadgeProps['variant']>> = {
  [AcrSeverity.CRITICAL]: 'destructive',
  [AcrSeverity.HIGH]: 'destructive',
  [AcrSeverity.LOW]: 'success',
  [AcrSeverity.MEDIUM]: 'warning',
};

/** Single-letter `[A]`/`[C]`/`[R]` badge text for the per-entry category marker. */
export const getAcrCategoryLetter = (category: AcrCategory): string => CATEGORY_LETTERS[category];

export const getAcrCategoryBadgeVariant = (category: AcrCategory): BadgeProps['variant'] =>
  CATEGORY_BADGE_VARIANT[category];

/** Hover-tooltip gloss for the category badge, explaining what A/C/R means. */
export const getAcrCategoryTooltip = (category: AcrCategory): string => CATEGORY_TOOLTIPS[category];

export const getAcrStatusLabel = (status: AcrStatus): string => STATUS_LABELS[status];

export const getAcrStatusBadgeVariant = (status: AcrStatus): BadgeProps['variant'] => STATUS_BADGE_VARIANT[status];

export const getAcrProbabilityLabel = (probability?: AcrProbability | null): string =>
  probability ? PROBABILITY_LABELS[probability] : '—';

export const getAcrSeverityLabel = (severity?: AcrSeverity | null): string =>
  severity ? SEVERITY_LABELS[severity] : '—';

export const getAcrSeverityBadgeVariant = (severity?: AcrSeverity | null): BadgeProps['variant'] =>
  severity ? SEVERITY_BADGE_VARIANT[severity] : 'pending';

/** An "open item" is any ACR entry whose status is Open — drives the header shield badge count. */
export const getAcrOpenCount = (entries: StudyAcrEntrySelectDto[]): number =>
  entries.filter((entry) => entry.status === AcrStatus.OPEN).length;

/**
 * Mirrors backend validator BV-048: open Risks with no mitigation block decision-maker (G4) sign-off.
 * The single client-side source for the shield's blocked indicator, the register banner, and the P5
 * gate preview — `getAcrG4Blocked` and the gate's named-reason list both derive from this.
 */
export const getAcrG4BlockingEntries = (entries: StudyAcrEntrySelectDto[]): StudyAcrEntrySelectDto[] =>
  entries.filter(
    (entry) =>
      entry.category === AcrCategory.RISK && entry.status === AcrStatus.OPEN && !entry.mitigationApproach?.trim(),
  );

export const getAcrG4Blocked = (entries: StudyAcrEntrySelectDto[]): boolean =>
  getAcrG4BlockingEntries(entries).length > 0;

export const ACR_CATEGORY_ORDER: readonly AcrCategory[] = [
  AcrCategory.ASSUMPTION,
  AcrCategory.CONSTRAINT,
  AcrCategory.RISK,
];

/** Status order for the register's STATUS filter chips. */
export const ACR_STATUS_ORDER: readonly AcrStatus[] = [
  AcrStatus.OPEN,
  AcrStatus.MITIGATED,
  AcrStatus.ACCEPTED,
  AcrStatus.CLOSED,
];

/** Shared between the inline entry form's `<form id>` and the register modal footer's submit button (`form={…}`). */
export const MEIA_ACR_ENTRY_FORM_ID = 'meia-acr-entry-form';

/** Per-category label for the `potentialImpact` field (mirrors the prototype's split impact fields). */
export const getAcrImpactLabel = (category: AcrCategory): string => {
  switch (category) {
    case AcrCategory.ASSUMPTION:
      return 'Risk if violated';
    case AcrCategory.CONSTRAINT:
      return 'Operational impact';
    default:
      return 'Potential impact';
  }
};
